import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthShell, AuthCard, authInputClass } from '../components/Common/AuthShell';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error(t('forgotPassword.enterEmail'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('forgotPassword.sendFailed'));
      }

      setEmailSent(true);
      toast.success(t('forgotPassword.linkSent'));
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error(error.message || t('forgotPassword.sendFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <AuthShell>
        <AuthCard>
          <div className="text-center">
            <div className="mb-5 inline-flex">
              <div className="grid h-16 w-16 place-items-center rounded-2xl bg-gradient-to-br from-lime-300 to-emerald-400 shadow-glow">
                <CheckCircle className="h-8 w-8 text-growth-ink" />
              </div>
            </div>
            <h1 className="mb-3 text-2xl font-black">{t('forgotPassword.checkEmail.title')}</h1>
            <p className="mb-6 text-white/[0.6]">
              {t('forgotPassword.checkEmail.sentTo')} <strong className="text-white">{email}</strong>
            </p>
          </div>

          <div className="mb-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-left">
            <h3 className="mb-3 font-bold text-cyan-200">{t('forgotPassword.checkEmail.nextTitle')}</h3>
            <ol className="space-y-2 text-sm text-white/70">
              {[1, 2, 3, 4].map((step) => (
                <li key={step} className="flex gap-2">
                  <span className="font-black text-lime-300">{step}.</span>
                  <span>{t(`forgotPassword.checkEmail.step${step}`)}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="space-y-3">
            <button type="button" onClick={() => navigate('/login')} className="landing-primary-cta w-full">
              {t('common.backToLogin')}
            </button>
            <button
              type="button"
              onClick={() => setEmailSent(false)}
              className="flex w-full items-center justify-center rounded-2xl border border-white/14 bg-white/[0.06] px-4 py-3 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.1]"
            >
              {t('forgotPassword.checkEmail.differentEmail')}
            </button>
          </div>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell tagline={t('forgotPassword.tagline')} back={{ label: t('common.backToLogin'), to: '/login' }}>
      <AuthCard>
        <div className="mb-6">
          <h2 className="mb-2 text-2xl font-black">{t('forgotPassword.title')}</h2>
          <p className="text-sm text-white/[0.6]">{t('forgotPassword.description')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.emailAddress')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Mail className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInputClass}
                placeholder={t('common.emailPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="landing-primary-cta w-full disabled:cursor-not-allowed disabled:opacity-60">
            {isLoading ? (
              <>
                <svg className="-ml-1 mr-1 h-5 w-5 animate-spin text-growth-ink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('forgotPassword.submitting')}
              </>
            ) : (
              <>
                <Mail className="h-5 w-5" />
                {t('forgotPassword.submit')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm font-semibold text-cyan-200 transition-colors hover:text-cyan-100">
            {t('common.linkBackToLogin')}
          </Link>
        </div>
      </AuthCard>
    </AuthShell>
  );
};
